/**
 * Hook that edits the text of a line item in a contract extraction category.
 *
 * Calls the `editContractLineItem` mutation (SE-4382) which rewrites
 * the `text` field of a single item at `(category, itemIndex)` within
 * the extraction's `extractedData` JSONB column and records an Activity
 * audit row with the old + new values. The GraphQL document is defined
 * inline via `gql` (same pattern used by the other extraction hooks in
 * this feature) so it can ship before codegen runs against the updated
 * schema.
 * @remarks Refetches the `GetContractExtraction` query on success so
 * the updated text renders in the review view immediately and any
 * server-side changes to `isConflict` / `confidenceNote` flow through
 * to the UI. Consumed by `ExtractionDataSectionContainer` in SE-4384.
 * @module features/contracts/hooks/useEditLineItem
 */

import { gql, useMutation } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { activeContractExtractionRefetch } from "./contractExtractionRefetch";

/** GraphQL document for the EditContractLineItem mutation. */
export const EDIT_CONTRACT_LINE_ITEM_MUTATION = gql`
  mutation EditContractLineItem($input: EditContractLineItemInput!) {
    editContractLineItem(input: $input) {
      success
    }
  }
`;

/** Shape of the EditContractLineItem mutation response. */
interface EditContractLineItemResponse {
  readonly editContractLineItem: { readonly success: boolean };
}

/** Shape of the EditContractLineItem mutation variables. */
interface EditContractLineItemVariables {
  readonly input: {
    readonly extractionId: string;
    readonly category: string;
    readonly itemIndex: number;
    readonly newText: string;
  };
}

/** Public API returned by `useEditLineItem`. */
export interface UseEditLineItemResult {
  /**
   * Edits the text of a line item in a category within the extraction
   * data. On success, refetches the `GetContractExtraction` query so
   * the updated item (and any server-side conflict/confidence changes)
   * renders in the review view.
   * @param extractionId - UUID of the extraction to update
   * @param category - Category key within `extractedData`
   * @param itemIndex - Zero-based index of the item within the category
   * @param newText - New free-text value for the line item
   * @param playerId - Player UUID (needed for the extraction query refetch variables)
   * @param documentType - Document type (needed for the extraction query refetch variables)
   * @returns `true` when the server confirms success, `false` otherwise
   */
  readonly editLineItem: (
    extractionId: string,
    category: string,
    itemIndex: number,
    newText: string,
    playerId: string,
    documentType: string
  ) => Promise<boolean>;
  /** Whether an edit request is currently in flight. */
  readonly isEditing: boolean;
}

/**
 * Wraps the EditContractLineItem mutation with loading state and an
 * automatic refetch of the extraction query on success.
 * @returns Imperative edit function + loading state
 */
export function useEditLineItem(): UseEditLineItemResult {
  const [runMutation] = useMutation<
    EditContractLineItemResponse,
    EditContractLineItemVariables
  >(EDIT_CONTRACT_LINE_ITEM_MUTATION);

  const [isEditing, setIsEditing] = useState(false);

  const editLineItem = useCallback(
    async (
      extractionId: string,
      category: string,
      itemIndex: number,
      newText: string,
      playerId: string,
      documentType: string
    ): Promise<boolean> => {
      setIsEditing(true);
      try {
        const { data } = await runMutation({
          variables: {
            input: { extractionId, category, itemIndex, newText },
          },
          refetchQueries: [
            activeContractExtractionRefetch(playerId, documentType),
          ],
          awaitRefetchQueries: true,
        });
        return data?.editContractLineItem?.success === true;
      } catch {
        return false;
      } finally {
        setIsEditing(false);
      }
    },
    [runMutation]
  );

  return useMemo(
    () => ({ editLineItem, isEditing }),
    [editLineItem, isEditing]
  );
}
